import { packageEvents, PackageDeliveryIssueV2, PackageDelayedIssue } from '@consumer/shared'
import { PackageEventOperations } from '@consumer/graphql'

export const getIssueDetailText = (
  clientDisplayName: string,
  event: PackageEventOperations,
  message: string | null | undefined
): string => {
  switch (event) {
    case packageEvents.MISDELIVERED:
      return message?.includes('Delivered to wrong location')
        ? `We're sorry that your package was not delivered to the expected location. We are working with the driver to go back and retrieve the package. Sometimes, packages are accidentally delivered to neighbors, or other members of your household, if you would like to check with them. We do not recommend going to find the package if you have health or safety concerns about doing so. For questions, we recommend you text Veho Customer Support at ${
            import.meta.env.REACT_APP_SMS_SHORTCODE
          }.`
        : `Unfortunately, your package has been lost in transit. We apologize for the inconvenience. Please reach out to the merchant so they may assist you with a resolution.`
    case packageEvents.DISCARDED:
      return `Your package from ${clientDisplayName} was unable to be delivered. We have let ${clientDisplayName} know. If you have questions or need help, please text Veho Customer Support at ${
        import.meta.env.REACT_APP_SMS_SHORTCODE
      }.`
    case packageEvents.NOT_RECEIVED_FROM_CLIENT:
      return `We didn't receive your package from ${clientDisplayName} when we expected to. For details on the status of your order, we recommend reaching out to ${clientDisplayName}.`
    case packageEvents.RETURNED_TO_CLIENT:
      return `Your order is being returned to ${clientDisplayName}, because you or ${clientDisplayName} requested it, or because we weren't able to deliver it. If you have questions, please reach out to ${clientDisplayName}.`
    case packageEvents.PACKAGE_DELAYED:
      return "We're sorry for the delay. We're working to get your package back on track!"
    case packageEvents.CANCELLED:
      return `This order has been canceled. For more details, we recommend reaching out to ${clientDisplayName}.`
    case packageEvents.WRONG_FACILITY_BY_CLIENT:
    case packageEvents.WRONG_FACILITY_BY_VEHO:
      return "We regret to inform you that a transit error has delayed your package. Rest assured we are actively working to correct the issue and get your package moving. We'll update your delivery date as soon as the package is back on track."
    default:
      return ''
  }
}

export const getDelayDetailText = (delayType: string) => {
  switch (delayType) {
    case PackageDelayedIssue.accessIssueFrontGate:
    case PackageDelayedIssue.accessIssueBuildingOrDoorCode:
    case PackageDelayedIssue.accessIssueSchoolCampus:
      return 'To ensure a successful delivery, please provide your access code or detailed entry instructions for the driver. Once we are able to process the package for redelivery, we will provide an updated delivery date and window.'
    case PackageDelayedIssue.wrongLocation:
      return 'Please double-check your address for accuracy and add any details that might help the delivery driver, like nearby cross-streets or unique building descriptions. Once we are able to process the package for redelivery, we will provide an updated delivery date and window.'
    case PackageDelayedIssue.closedLocationStoreFront:
    case PackageDelayedIssue.closedLocationOffice:
    case PackageDelayedIssue.closedLocationLeasingOffice:
    case PackageDelayedIssue.closedLocationMailroom:
      return 'To ensure a successful delivery, please provide an alternate delivery location or confirm the original location’s operating hours. Once we are able to process the package for redelivery, we will provide an updated delivery date and window.'
    case PackageDelayedIssue.customerRefused:
      return `The Veho driver partner reported that the delivery was refused by someone at the delivery location. If you believe this was a mistake, please contact Veho Support by texting ${
        import.meta.env.REACT_APP_SMS_SHORTCODE
      }.`
    case PackageDelayedIssue.damagedPackage:
      return 'The Veho driver partner reported that your package was damaged and was not fit for delivery. We sincerely apologize for the inconvenience. For assistance with a refund or replacement, please contact Veho Support so that we can coordinate with the merchant.'
    case PackageDelayedIssue.unsafeConditionsPetOnSite:
    case PackageDelayedIssue.unsafeConditionsDarkUnlitArea:
      return "The Veho driver partner encountered a safety concern at or near the delivery location and couldn't complete your delivery. Once we are able to process the package for redelivery, we will provide an updated delivery date and window."
    case PackageDelayedIssue.unsafeConditionsWeatherRelated:
      return "We prioritize the safety of Veho driver partners and your package. We'll provide a new delivery time as soon as possible."
    case PackageDelayedIssue.militaryBase:
    case PackageDeliveryIssueV2.noAccessClosedMilitaryBase:
      return `Your delivery address was identified as a military base, which only USPS is able to deliver to. We apologize for the inconvenience. If the driver was mistaken or you want to update your address, please contact Veho Support by texting ${
        import.meta.env.REACT_APP_SMS_SHORTCODE
      }`
    // V2: access issue (door code / locked gate → same copy as V1 access issues)
    case PackageDeliveryIssueV2.noAccessClosedDoorCode:
    case PackageDeliveryIssueV2.noAccessClosedLockedGate:
      return 'To ensure a successful delivery, please provide your access code or detailed entry instructions for the driver. Once we are able to process the package for redelivery, we will provide an updated delivery date and window.'
    // V2: closed location (after-hours → same copy as V1 closed location)
    case PackageDeliveryIssueV2.noAccessClosedClosedAfterHours:
      return 'To ensure a successful delivery, please provide an alternate delivery location or confirm the original location\u2019s operating hours. Once we are able to process the package for redelivery, we will provide an updated delivery date and window.'
    // V2: can't locate (wrong location / missing unit → same copy as V1 wrong location)
    case PackageDeliveryIssueV2.cantLocateWrongLocation:
    case PackageDeliveryIssueV2.cantLocateMissingUnitNumber:
      return 'Please double-check your address for accuracy and add any details that might help the delivery driver, like nearby cross-streets or unique building descriptions. Once we are able to process the package for redelivery, we will provide an updated delivery date and window.'
    // V2: safety (animal / unlit / hostile → same copy as V1 unsafe conditions)
    case PackageDeliveryIssueV2.safetyAnimalPet:
    case PackageDeliveryIssueV2.safetyUnlitArea:
    case PackageDeliveryIssueV2.safetyHostilePerson:
      return "The Veho driver partner encountered a safety concern at or near the delivery location and couldn't complete your delivery. Once we are able to process the package for redelivery, we will provide an updated delivery date and window."
    // V2: weather → same copy as V1 weather-related unsafe conditions
    case PackageDeliveryIssueV2.weather:
      return "We prioritize the safety of Veho driver partners and your package. We'll provide a new delivery time as soon as possible."
    // V2: customer refused → same copy as V1
    case PackageDeliveryIssueV2.customerRefused:
      return `The Veho driver partner reported that the delivery was refused by someone at the delivery location. If you believe this was a mistake, please contact Veho Support by texting ${
        import.meta.env.REACT_APP_SMS_SHORTCODE
      }.`
    case PackageDelayedIssue.packageDelayed:
    case PackageDelayedIssue.ranOutOfTimeRouteStartedLate:
    case PackageDelayedIssue.ranOutOfTimeRouteTookLongerThanEstimated:
    case PackageDelayedIssue.ranOutOfTimeAppIssues:
    case PackageDelayedIssue.other:
    case PackageDeliveryIssueV2.vehicleAccident: // V2: driver/system-fault reasons (hidden on mobile; generic on web)
    case PackageDeliveryIssueV2.vehicleMalfunction:
    case PackageDeliveryIssueV2.outOfTime:
    case PackageDeliveryIssueV2.appIssue:
    case PackageDeliveryIssueV2.personal:
    case PackageDeliveryIssueV2.customerNotAvailable: // TODO(CBP-4386): customer-actionable but no specific copy yet
    case PackageDeliveryIssueV2.invalidDeliveryPin:
    case PackageDeliveryIssueV2.other:
    default:
      return 'The delivery driver ran into an unforeseen issue during their route. We apologize for the inconvenience. Rest assured we will get your package on the next available route. Once we are able to process the package for redelivery, we will provide an updated delivery date and window.'
  }
}

const eventsWithDelayDetails = [PackageEventOperations.PackageHadDeliveryIssue, PackageEventOperations.PackageDelayed]

export const getEventDetailText = (
  clientDisplayName: string,
  event: PackageEventOperations,
  message: string | null | undefined
): string => {
  return eventsWithDelayDetails.includes(event)
    ? getDelayDetailText(message ?? '')
    : getIssueDetailText(clientDisplayName, event, message)
}
